
import { useState, useEffect, useCallback } from 'react';
import { useOnboardingContext } from '@/context/OnboardingContext';
import { saveGameProgressToSupabase } from '@/lib/supabase/game';

// Import all the refactored modules
import { DEFAULT_GAME_DATA } from './game-data/constants';
import type { GameData, SyncStatus } from './game-data/types';
import { useGameDataStorage } from './game-data/storage';
import { useGameDataValidation } from './game-data/validation';
import { useGameDataSync } from './game-data/sync';
import { useGameDataActions } from './game-data/actions';

// Re-export types for backward compatibility
export type { GameData };

export const useGameData = () => {
  const { userData } = useOnboardingContext() || { userData: null };
  const [gameData, setGameData] = useState<GameData>(DEFAULT_GAME_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSyncTime, setLastSyncTime] = useState<number>(0);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('never');

  // Stabilize state setters with useCallback to prevent dependency changes
  const stableSetGameData = useCallback((data: GameData) => setGameData(data), []);
  const stableSetLastSyncTime = useCallback((time: number) => setLastSyncTime(time), []);
  const stableSetSyncStatus = useCallback((status: SyncStatus) => setSyncStatus(status), []);
  const stableSetIsLoading = useCallback((loading: boolean) => setIsLoading(loading), []);

  // Use the refactored modules
  const { loadFromLocalStorage, saveToLocalStorage } = useGameDataStorage(userData);
  const { hasGameProgress } = useGameDataValidation();
  const { loadGameData, forceSyncData } = useGameDataSync(
    userData,
    loadFromLocalStorage,
    saveToLocalStorage,
    hasGameProgress
  );

  /**
   * Update game data both locally and in Supabase
   */
  const updateGameData = useCallback(async (newData: Partial<GameData>) => {
    const updatedData = { ...gameData, ...newData };
    console.log('🎮 Game Data - Updating data:', updatedData);
    
    setGameData(updatedData);
    saveToLocalStorage(updatedData);

    // Save to Supabase if user is logged in
    if (userData?.email) {
      try {
        const success = await saveGameProgressToSupabase(userData.email, updatedData, userData);
        if (success) {
          const syncTime = Date.now();
          setLastSyncTime(syncTime);
          setSyncStatus(hasGameProgress(updatedData) ? 'with_data' : 'empty');
          console.log('🎮 Game Data - Updated and synced, sync time:', syncTime);
        } else {
          setSyncStatus('error');
        }
      } catch (error) {
        console.error('🎮 Game Data - Error syncing to Supabase:', error);
        setSyncStatus('error');
      }
    }
  }, [gameData, userData, saveToLocalStorage, hasGameProgress]);

  // Use the actions module
  const { recordGameResult, unlockFact } = useGameDataActions(gameData, updateGameData, userData);

  /**
   * Force refresh data from Supabase
   */
  const refreshGameData = useCallback(async () => {
    console.log('🎮 Game Data - Force refreshing data');
    await forceSyncData(stableSetGameData, stableSetLastSyncTime, stableSetSyncStatus, stableSetIsLoading);
  }, [forceSyncData, stableSetGameData, stableSetLastSyncTime, stableSetSyncStatus, stableSetIsLoading]);

  /**
   * Get current game data with real-time values
   */
  const getCurrentGameData = useCallback(() => {
    return gameData;
  }, [gameData]);

  // Load data when user changes or on mount - simplified with stable dependencies
  useEffect(() => {
    const userEmail = userData?.email;
    
    if (userEmail) {
      console.log('🎮 Game Data - User changed or first load, initializing...');
      loadGameData(stableSetGameData, stableSetLastSyncTime, stableSetSyncStatus, stableSetIsLoading);
    } else if (!userEmail) {
      // Reset when no user
      console.log('🎮 Game Data - No user, resetting to default state');
      setIsLoading(false);
      setSyncStatus('never');
      setGameData(DEFAULT_GAME_DATA);
    }
  }, [userData?.email, loadGameData, stableSetGameData, stableSetLastSyncTime, stableSetSyncStatus, stableSetIsLoading]);

  // Listen for onboarding completion to trigger fresh sync
  useEffect(() => {
    const handleOnboardingCompleted = (event: CustomEvent) => {
      const { userData: newUserData } = event.detail;
      console.log('🎮 Game Data - Onboarding completed event received for:', newUserData?.email);
      
      if (newUserData?.email) {
        // Force a fresh sync after onboarding
        setTimeout(() => {
          console.log('🎮 Game Data - Forcing fresh sync after onboarding');
          refreshGameData();
        }, 1000); // Small delay to ensure everything is ready
      }
    };

    window.addEventListener('onboardingCompleted', handleOnboardingCompleted as EventListener);
    
    return () => {
      window.removeEventListener('onboardingCompleted', handleOnboardingCompleted as EventListener);
    };
  }, [refreshGameData]);

  return {
    gameData,
    isLoading,
    updateGameData,
    recordGameResult,
    unlockFact,
    refreshGameData,
    getCurrentGameData,
    lastSyncTime,
    syncStatus,
    forceSyncData: refreshGameData
  };
};
